import React, { useContext, useRef } from "react";
import { ResizeEnable, Rnd } from "react-rnd";
import { CanvasContext, ICanvasComponent } from "../canvasContext.tsx";
import { resizeHandleClasses } from "../canvasUtils.tsx";
import ImageElement from "./ImageElement.tsx";
import TextElement from "./TextElement.tsx";
import VideoElement from "./VideoElement.tsx";
import SlideElement from "./SlideElement.tsx";
import Model3DElement from "./Model3DElement.tsx";
import ElementSelectionToolbar, {
  DRAG_HANDLE_CLASS,
} from "./ElementSelectionToolbar.tsx";
import "./canvasSelection.css";

const STAGE_MARGIN = 48;

const componentMap: { [key: string]: React.ComponentType<ICanvasComponent> } = {
  TEXT: TextElement,
  IMAGE: ImageElement,
  VIDEO: VideoElement,
  SLIDE: SlideElement,
  "3D_MODEL": Model3DElement,
};

const toCssSize = (value?: string | number) => {
  if (value == null) return undefined;
  if (typeof value === "number") return `${value}px`;
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return /^\d+(\.\d+)?$/.test(trimmed) ? `${trimmed}px` : trimmed;
};

const parsePx = (value?: string | number) => {
  if (typeof value === "number") return value;
  if (value == null) return 0;
  const n = parseFloat(String(value));
  return Number.isFinite(n) ? n : 0;
};

const getEnableResize = (type: string): ResizeEnable => {
  const allowAll = ["IMAGE", "VIDEO", "SLIDE", "3D_MODEL"].includes(type);
  return {
    bottom: allowAll,
    bottomLeft: true,
    bottomRight: true,
    top: allowAll,
    topLeft: true,
    topRight: true,
    left: true,
    right: true,
  };
};

const headerLabelForType = (type?: string) => {
  if (type === "SLIDE") return "Slide";
  if (type === "3D_MODEL") return "3D";
  if (type === "IMAGE") return "Image";
  if (type === "VIDEO") return "Video";
  if (type === "TEXT") return "Text";
  return "Element";
};

const CanvasComponent = (
  props: ICanvasComponent & { viewMode?: "web" | "mobile"; stageScale?: number }
) => {
  const { state, actions } = useContext(CanvasContext);
  const {
    dimension,
    position,
    mobilePosition,
    mobileDimension,
    content,
    id,
    type,
    slideZoom,
    cameraOrbit,
    isBackground,
    isPlayMode,
    slideAnnotations,
    imageFit,
    rotation,
    viewMode = "web",
    stageScale = 1,
  } = props;
  const [showGrids, setShowGrids] = React.useState(false);
  const [isReadOnly, setIsReadOnly] = React.useState(true);
  const [isDraggingActive, setIsDraggingActive] = React.useState(false);
  const elementRef = React.useRef<HTMLDivElement>(null);
  const isDragged = useRef<boolean>(false);

  const activeSelection = state?.activeSelection;
  const canvasData = state?.canvasData ?? [];
  const layerIndex = id ? canvasData.findIndex((c) => c.id === id) : -1;
  const canMoveUp = layerIndex >= 0 && layerIndex < canvasData.length - 1;
  const canMoveDown = layerIndex > 0;
  const isSelected = Boolean(id && activeSelection?.has(id));
  const showToolbar =
    !isBackground &&
    !isPlayMode &&
    isSelected &&
    (activeSelection?.size || 0) === 1;

  const onBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const toolbarElement = document.querySelector("#toolbar");
    if (
      event.currentTarget.contains(event?.relatedTarget as Element) ||
      toolbarElement?.contains(event?.relatedTarget as Element)
    ) {
      return;
    }
    setIsReadOnly(true);
    actions?.setEnableQuillToolbar(false);
    if (id && activeSelection) {
      activeSelection.delete(id);
      actions?.setActiveSelection(new Set(activeSelection));
    }
    actions?.saveNow?.();
  };

  const effectivePosition =
    viewMode === "mobile" ? mobilePosition ?? position : position;

  const effectiveDimension =
    viewMode === "mobile" ? mobileDimension ?? dimension : dimension;

  const getComponent = () => {
    const Component = type && componentMap[type];
    if (!Component || !id) return null;
    return (
      <Component
        key={id}
        id={id}
        type={type}
        position={effectivePosition}
        dimension={effectiveDimension}
        content={content}
        isReadOnly={isPlayMode ? true : isReadOnly}
        isBackground={isBackground}
        isPlayMode={isPlayMode}
        imageFit={imageFit}
        rotation={rotation}
        slideZoom={slideZoom}
        cameraOrbit={cameraOrbit}
        slideAnnotations={slideAnnotations}
      />
    );
  };

  const rndClassName = [
    isSelected || isDragged.current ? "canvas-element-selected" : "",
    showGrids && !isSelected ? "canvas-element-hover" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const style: React.CSSProperties = {
    outline: "none",
    border: "1.5px solid transparent",
  };

  const onMouseEnter = () => {
    setShowGrids(true);
  };

  const onMouseLeave = () => {
    setShowGrids(false);
  };

  const onfocus = () => {
    if (id) {
      actions?.setActiveSelection(new Set(state?.activeSelection.add(id)));
    }
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!isReadOnly) event.stopPropagation();
  };

  const handleClass =
    !isBackground &&
    id &&
    state?.activeSelection.has(id) &&
    state?.activeSelection.size === 1
      ? "showHandles"
      : "";

  const onDoubleClick = () => {
    if (!isReadOnly) return;
    setIsReadOnly(false);
    actions?.setEnableQuillToolbar(true);
  };

  const fitToSlideWidth = () => {
    if (!id) return;
    const stageWidth =
      Number(
        (elementRef.current?.closest("[data-stage-width]") as HTMLElement | null)
          ?.dataset?.stageWidth
      ) || 1280;
    const nextWidth = Math.max(120, stageWidth - STAGE_MARGIN * 2);
    const nextLeft = STAGE_MARGIN;
    const height = parsePx(effectiveDimension?.height) || 120;
    if (viewMode === "mobile") {
      actions?.updateCanvasData({
        id,
        mobilePosition: { left: nextLeft, top: effectivePosition?.top || 0 },
        mobileDimension: {
          width: `${nextWidth}px`,
          height: `${height}px`,
        },
      });
    } else {
      actions?.updateCanvasData({
        id,
        position: { left: nextLeft, top: effectivePosition?.top || 0 },
        dimension: {
          width: `${nextWidth}px`,
          height: `${height}px`,
        },
      });
    }
    actions?.saveNow?.();
  };

  React.useEffect(() => {
    if (!isDraggingActive) return;
    const stopDragging = () => {
      actions?.endHistoryStep?.();
      isDragged.current = false;
      setIsDraggingActive(false);
    };
    window.addEventListener("mouseup", stopDragging);
    window.addEventListener("blur", stopDragging);
    return () => {
      window.removeEventListener("mouseup", stopDragging);
      window.removeEventListener("blur", stopDragging);
    };
  }, [isDraggingActive]);

  if (isPlayMode) {
    return (
      <div
        style={{
          position: "absolute",
          left: toCssSize(position?.left) || 0,
          top: toCssSize(position?.top) || 0,
          width: toCssSize(dimension?.width) || 0,
          height: toCssSize(dimension?.height) || 0,
          overflow: "hidden",
          pointerEvents: "auto",
          transform: rotation ? `rotate(${rotation}deg)` : undefined,
          transformOrigin: "center center",
        }}
      >
        <div style={{ position: "relative", width: "100%", height: "100%" }}>
          <div
            className="item-container"
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              zIndex: 0,
            }}
          >
            {getComponent()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={elementRef}>
      <Rnd
        className={rndClassName}
        style={style}
        scale={stageScale > 0 ? stageScale : 1}
        size={{
          width: parsePx(effectiveDimension?.width) || 0,
          height: parsePx(effectiveDimension?.height) || 0,
        }}
        position={{
          x: parsePx(effectivePosition?.left),
          y: parsePx(effectivePosition?.top),
        }}
        onDragStart={() => {
          actions?.beginHistoryStep?.();
          isDragged.current = true;
          setIsDraggingActive(true);
        }}
        onDragStop={(e, d) => {
          isDragged.current = false;
          setIsDraggingActive(false);
          actions?.endHistoryStep?.();
          if (viewMode === "mobile") {
            actions?.updateCanvasData({
              id,
              mobilePosition: { left: d.x, top: d.y },
            });
          } else {
            actions?.updateCanvasData({
              id,
              position: { left: d.x, top: d.y },
            });
          }
          actions?.saveNow?.();
        }}
        resizeHandleWrapperClass={handleClass}
        resizeHandleClasses={resizeHandleClasses}
        onResize={(e, direction, ref, delta, position) => {
          if (viewMode === "mobile") {
            actions?.updateCanvasData({
              id,
              mobileDimension: {
                width: ref.style.width,
                height: ref.style.height,
              },
              mobilePosition: { top: position.y, left: position.x },
            });
          } else {
            actions?.updateCanvasData({
              id,
              dimension: {
                width: ref.style.width,
                height: ref.style.height,
              },
              position: { top: position.y, left: position.x },
            });
          }
        }}
        onResizeStop={() => {
          actions?.endHistoryStep?.();
          actions?.saveNow?.();
        }}
        onResizeStart={() => {
          actions?.beginHistoryStep?.();
        }}
        enableResizing={isBackground ? false : getEnableResize(type)}
        minWidth={100}
        minHeight={50}
        // Text edit mode locks drag so caret/selection works; viewers keep a drag-handle.
        disableDragging={
          !!isBackground || (type === "TEXT" && !isReadOnly)
        }
        dragHandleClassName={
          type === "SLIDE" || type === "3D_MODEL" || type === "VIDEO" || !isReadOnly
            ? DRAG_HANDLE_CLASS
            : undefined
        }
        cancel=".canvas-element-toolbar__btn, .canvas-element-toolbar__menu, .canvas-element-toolbar__menu-item, .canvas-media-upload, button, input"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onDoubleClick={isBackground ? undefined : onDoubleClick}
        onKeyDown={onKeyDown}
        onFocus={isBackground ? undefined : onfocus}
        onBlur={isBackground ? undefined : onBlur}
        tabIndex={isBackground ? -1 : 0}
        lockAspectRatio={false}
      >
        <div style={{ position: "relative", width: "100%", height: "100%" }}>
          {showToolbar && id ? (
            <ElementSelectionToolbar
              label={headerLabelForType(type)}
              canMoveUp={canMoveUp}
              canMoveDown={canMoveDown}
              onDuplicate={() => actions?.duplicateElement?.(id)}
              onDelete={() => actions?.deleteElementById(id)}
              onMoveLayer={(direction) => actions?.moveElementLayer?.(id, direction)}
              onFitWidth={fitToSlideWidth}
              onBringToFront={() => actions?.bringElementToFront?.(id)}
              onSendToBack={() => actions?.sendElementToBack?.(id)}
            />
          ) : null}
          <div
            className="item-container"
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              zIndex: 0,
              transform: rotation ? `rotate(${rotation}deg)` : undefined,
              transformOrigin: "center center",
            }}
          >
            {isDraggingActive && (type === "SLIDE" || type === "3D_MODEL") && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 2,
                  background: "transparent",
                  cursor: "move",
                }}
              />
            )}
            {getComponent()}
          </div>
        </div>
      </Rnd>
    </div>
  );
};

export default CanvasComponent;
